import { Button, InputProps, TextField, Typography } from '@mui/material';
import React, { FormEventHandler, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { NavLink as RouterLink } from 'react-router-dom';
import { of } from 'rxjs';
import { AjaxError } from 'rxjs/ajax';
import { catchError } from 'rxjs/operators';

import { If } from 'app/components/if';
import { HttpCodes } from 'app/constants/http-codes';
import { Category } from 'app/model/cris/category';
import { categoryService } from 'app/services/category.service';
import { messagingService } from 'app/services/messaging.service';
import { ModelWithoutId } from 'app/type';

import { selectProdDataStatus } from '../cris.slice';

export const CategoryForm: React.FunctionComponent = () => {
    const { id } = useParams<{ id: string }>();
    const history = useHistory();

    const [category, setCategory] = useState<ModelWithoutId<Category>>({ category: '', dateCreated: 0 });

    const isNew = 'new' === id;

    const showProd = useSelector(selectProdDataStatus);

    const handleSubmit: FormEventHandler = (evt) => {
        evt.preventDefault();
        categoryService.save(category).subscribe((cat) => {
            history.replace(`/cris/categories/${cat.id}`);
        });
    };

    const handleCategoryChange: InputProps['onChange'] = (evt) => {
        setCategory({ ...category, [evt.target.name]: evt.target.value });
    };

    const deployToProd = (): void => {
        if (!category.id) {
            messagingService.showError('Save the category first');

            return;
        }

        categoryService.deploy(category as Category).subscribe(() => {
            messagingService.showSuccess('Deployed');
        });
    };

    useEffect(() => {
        if (!isNew) {
            categoryService
                .get(id)
                .pipe(
                    catchError((err: AjaxError) => {
                        if (err.status === HttpCodes.NOT_FOUND.valueOf()) {
                            return of({ category: '', dateCreated: 0 });
                        }
                        throw err;
                    }),
                )
                .subscribe(setCategory);
        }
    }, [id, isNew, showProd]);

    return (
        <form onSubmit={handleSubmit}>
            <Typography variant='h6'>
                {isNew ? 'Create' : 'Edit'} Category{isNew ? '' : `: ${id}`}
            </Typography>
            <div>
                <TextField name='category' label='Category' value={category.category} onChange={handleCategoryChange} />
            </div>
            <If condition={undefined !== category.id && !showProd}>
                <Button type='submit' variant='outlined' color='primary' className='mt-2'>
                    Save
                </Button>
            </If>
            <Button
                type='button'
                variant='outlined'
                color='inherit'
                className='mt-2 ml-1'
                component={RouterLink}
                to='/cris/categories'
            >
                Cancel
            </Button>
            <If condition={undefined !== category.id && !showProd}>
                <Button type='button' variant='contained' color='success' className='mt-2 ml-1' onClick={deployToProd}>
                    Deploy Prod
                </Button>
            </If>
        </form>
    );
};
